import type { JSX, ReactNode } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { ArrowLeft } from 'lucide-react';
import { Badge } from '@caratuva/ui';
import type { CustomerBuyerDetailDTO } from '@caratuva/shared-types';
import { getPayerDetail } from '../lib/api';

/*
 * Payer detail — the read-only view of a self-signup payer (a global Buyer the
 * org has billed). Identity + KYC status + the org<->buyer billing relationship
 * (first/last invoiced, invoice count). Payers never collect, so — unlike the
 * connected-account detail — there is no bank-account / payout section. The
 * onboarded connected-account detail lives at /customers/$id.
 */

export const Route = createFileRoute('/_app/customers/payer/$id')({
  component: PayerDetail,
});

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive';

// KYC status is a plain string; map to a badge variant with a neutral default
// for unmapped values (the strict tsconfig types the lookup as `| undefined`).
const KYC_STATUS_VARIANT: Record<string, BadgeVariant> = {
  created: 'outline',
  pending: 'outline',
  verifying: 'secondary',
  submitted: 'secondary',
  approved: 'default',
  verified: 'default',
  rejected: 'destructive',
};

function kycStatusVariant(status: string): BadgeVariant {
  return KYC_STATUS_VARIANT[status] ?? 'secondary';
}

function PayerDetail(): JSX.Element {
  const intl = useIntl();
  const { id } = Route.useParams();
  const query = useQuery({
    queryKey: ['payer', id],
    queryFn: ({ signal }) => getPayerDetail(id, signal),
  });

  return (
    <section className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/customers"
        className="inline-flex items-center gap-1 text-sm text-navy-700 transition-colors hover:text-navy-900"
      >
        <ArrowLeft aria-hidden className="h-4 w-4" strokeWidth={1.5} />
        {intl.formatMessage({ id: 'customers.title', defaultMessage: 'Customers' })}
      </Link>

      {query.isPending ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'customers.payer.detail.loading',
            defaultMessage: 'Loading payer...',
          })}
        </p>
      ) : query.isError ? (
        <p className="text-sm text-danger">
          {intl.formatMessage({
            id: 'customers.payer.detail.error',
            defaultMessage: 'Failed to load payer.',
          })}
        </p>
      ) : (
        <PayerDetailBody payer={query.data} />
      )}
    </section>
  );
}

function PayerDetailBody({ payer }: { payer: CustomerBuyerDetailDTO }): JSX.Element {
  const intl = useIntl();
  const displayName = payer.name ?? payer.email;

  return (
    <>
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-navy-900">{displayName}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">
            {intl.formatMessage({
              id: 'customers.source.selfSignup',
              defaultMessage: 'Self-signup',
            })}
          </Badge>
          <Badge variant={kycStatusVariant(payer.kycStatus)}>{payer.kycStatus}</Badge>
        </div>
      </header>

      <div className="rounded-md border border-navy-200 bg-white p-5">
        <h2 className="mb-4 text-sm font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({
            id: 'customers.payer.detail.section.identity',
            defaultMessage: 'Identity',
          })}
        </h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <DetailField
            label={intl.formatMessage({
              id: 'customers.payer.detail.field.name',
              defaultMessage: 'Name',
            })}
          >
            {payer.name ?? '—'}
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.payer.detail.field.email',
              defaultMessage: 'Email',
            })}
          >
            {payer.email}
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.payer.detail.field.entityType',
              defaultMessage: 'Entity type',
            })}
          >
            <span className="capitalize">{payer.entityType}</span>
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.payer.detail.field.country',
              defaultMessage: 'Country',
            })}
          >
            {payer.country ?? '—'}
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.payer.detail.field.kycStatus',
              defaultMessage: 'KYC status',
            })}
          >
            {payer.kycStatus}
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.payer.detail.field.receiverId',
              defaultMessage: 'Receiver ID',
            })}
          >
            <span className="font-mono text-xs break-all">{payer.receiverId ?? '—'}</span>
          </DetailField>
        </dl>
      </div>

      <div className="rounded-md border border-navy-200 bg-white p-5">
        <h2 className="mb-4 text-sm font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({
            id: 'customers.payer.detail.section.relationship',
            defaultMessage: 'Billing relationship',
          })}
        </h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <DetailField
            label={intl.formatMessage({
              id: 'customers.payer.detail.field.firstInvoicedAt',
              defaultMessage: 'First invoiced',
            })}
          >
            {payer.firstInvoicedAt ? formatDate(payer.firstInvoicedAt, intl.locale) : '—'}
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.payer.detail.field.lastInvoicedAt',
              defaultMessage: 'Last invoiced',
            })}
          >
            {payer.lastInvoicedAt ? formatDate(payer.lastInvoicedAt, intl.locale) : '—'}
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.payer.detail.field.invoiceCount',
              defaultMessage: 'Invoices',
            })}
          >
            {payer.invoiceCount}
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.payer.detail.field.payerId',
              defaultMessage: 'Payer ID',
            })}
          >
            <span className="font-mono text-xs break-all">{payer.id}</span>
          </DetailField>
        </dl>
      </div>
    </>
  );
}

function DetailField({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-navy-600">{label}</dt>
      <dd className="mt-1 text-sm text-navy-900">{children}</dd>
    </div>
  );
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, {
      dateStyle: 'medium',
    });
  } catch {
    return iso;
  }
}
